import React, { useState, useRef, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import ApiLoader from "../common/loader";
import { routes } from "../routings";
import axios from "axios";
import { REACT_APP_API_KEY, REACT_APP_API_URL } from "../common/config";
import { toast } from "react-toastify";

// Signature Canvas Component
const SignatureCanvas = ({ signatureData, onSignatureChange }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && signatureData) {
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
        setHasSignature(true);
      };
      img.src = signatureData;
    }
  }, [signatureData]);

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    ctx.beginPath();
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    const x = (e.clientX || e.touches[0].clientX) - rect.left;
    const y = (e.clientY || e.touches[0].clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing) {
      setIsDrawing(false);
      const canvas = canvasRef.current;
      const dataUrl = canvas.toDataURL('image/png');
      onSignatureChange(dataUrl);
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onSignatureChange('');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
        style={{
          border: '2px solid #ccc',
          borderRadius: '4px',
          cursor: 'crosshair',
          touchAction: 'none',
          width: '100%',
          maxWidth: '500px',
          height: '150px',
          backgroundColor: '#fff'
        }}
      />
      {hasSignature && (
        <button
          type="button"
          onClick={clearSignature}
          className="btn btn-sm btn-danger"
          style={{ marginTop: '10px' }}
        >
          Clear Signature
        </button>
      )}
    </div>
  );
};

const BootAuthorizationForm = () => {
  const { token } = useParams();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [error, setError] = useState(null);
  const [signatureType, setSignatureType] = useState('draw');
  const [signatureImage, setSignatureImage] = useState('');
  const [signatureText, setSignatureText] = useState('');
  const [certificationChecked, setCertificationChecked] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloadLink, setDownloadLink] = useState('');

  // Fetch authorization data on mount
  useEffect(() => {
    if (!token) {
      setError('Invalid authorization link');
      setLoading(false);
      return;
    }

    fetchAuthorizationData();
  }, [token]);

  const fetchAuthorizationData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${REACT_APP_API_URL}boot-authorization/${token}`,
        {
          headers: {
            'api-key': REACT_APP_API_KEY,
          },
        }
      );

      if (response.data.success) {
        setFormData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load authorization data');
      }
    } catch (err) {
      console.error('Error fetching authorization data:', err);
      if (err.response?.status === 410) {
        setError('This authorization link has expired');
      } else if (err.response?.status === 404) {
        setError('Invalid authorization link');
      } else {
        setError(err.response?.data?.message || 'Failed to load authorization data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignatureComplete = () => {
    if (signatureType === 'text' && signatureText.trim()) {
      // Create signature from text
      const canvas = document.createElement('canvas');
      canvas.width = 500;
      canvas.height = 150;
      const ctx = canvas.getContext('2d');
      
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      ctx.font = '30px "Brush Script MT", cursive';
      ctx.fillStyle = '#000000';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'bottom';
      ctx.fillText(signatureText, 10, canvas.height - 10);
      
      const dataUrl = canvas.toDataURL('image/png');
      setSignatureImage(dataUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!certificationChecked) {
      toast.error('You must certify that you meet the definition of a property owner');
      return;
    }

    if (signatureType === 'draw' && !signatureImage) {
      toast.error('Please provide your signature');
      return;
    }

    if (signatureType === 'text' && !signatureText.trim()) {
      toast.error('Please type your signature');
      return;
    }

    // Generate signature image from text if needed
    if (signatureType === 'text' && !signatureImage) {
      handleSignatureComplete();
      // Wait a moment for signature to be generated
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${REACT_APP_API_URL}boot-authorization/submit`,
        {
          token,
          signature_type: signatureType,
          signature_image: signatureImage,
          certification_checked: certificationChecked,
        },
        {
          headers: {
            'api-key': REACT_APP_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 200 && response.data.success) {
        setDownloadLink(response.data.data.form_url || '');
        setSubmitted(true);
        toast.success("Authorization submitted successfully!");
      } else {
        toast.error(response.data.message || "Failed to submit authorization. Please try again.");
      }
    } catch (err) {
      console.error("Error submitting authorization:", err);
      toast.error(err.response?.data?.message || "Failed to submit authorization. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading && !formData) {
    return (
      <section className="main_section">
        <ApiLoader />
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section className="main_section">
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-md-offset-2" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <div style={{ 
                backgroundColor: '#fff3cd', 
                padding: '40px', 
                borderRadius: '8px',
                border: '2px solid #ffc107'
              }}>
                <i className="fas fa-exclamation-triangle" style={{ fontSize: '64px', color: '#ffc107', marginBottom: '20px' }}></i>
                <h2 style={{ color: '#856404', marginBottom: '20px' }}>Authorization Unavailable</h2>
                <p style={{ fontSize: '18px', marginBottom: '30px', color: '#000' }}>
                  {error}
                </p>
                <a href={routes.home.path} className="btn yellow_btn">
                  Return to Home
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Success state
  if (submitted) {
    return (
      <section className="main_section">
        <div className="container">
          <div className="row">
            <div className="col-md-8 col-md-offset-2" style={{ textAlign: 'center', padding: '50px 20px' }}>
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '40px', 
                borderRadius: '8px',
                border: '2px solid #28a745'
              }}>
                <i className="fas fa-check-circle" style={{ fontSize: '64px', color: '#28a745', marginBottom: '20px' }}></i>
                <h2 style={{ color: '#28a745', marginBottom: '20px' }}>Authorization Submitted Successfully!</h2>
                <p style={{ fontSize: '18px', marginBottom: '30px' }}>
                  Thank you for completing the boot authorization.
                </p>
                {downloadLink && (
                  <div style={{ marginBottom: '30px' }}>
                    <a 
                      href={downloadLink} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn yellow_btn"
                      style={{ marginRight: '10px' }}
                    >
                      <i className="fas fa-download"></i> Download Authorization Form
                    </a>
                  </div>
                )}
                <a href={routes.home.path} className="btn btn-default">
                  Return to Home
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Main form
  return (
    <div>
      {loading && <ApiLoader />}
      <section className="main_section">
        <div className="container">
          <div className="row">
            <div className="col-md-10 col-md-offset-1">
              <div style={{ marginBottom: '30px' }}>
                <h2>Boot Authorization Form</h2>
                <p style={{ marginTop: '15px', fontSize: '14px', lineHeight: '1.6', color: '#666' }}>
                  Please review the information below and provide your authorization for the boot placement.
                </p>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Property and Violation Information */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  padding: '20px', 
                  marginBottom: '30px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}>
                  <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                    Property & Violation Information
                  </h4>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Date</label>
                        <input
                          type="text"
                          className="form-control"
                          value={new Date().toLocaleDateString('en-US', { timeZone: 'America/Denver' })}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Time (MT)</label>
                        <input
                          type="text"
                          className="form-control"
                          value={new Date().toLocaleTimeString('en-US', { timeZone: 'America/Denver', hour12: false })}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Property Address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData?.property_address || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Reason</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData?.reason || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </div>
                </div>

                {/* Vehicle Information */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  padding: '20px', 
                  marginBottom: '30px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}>
                  <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                    Vehicle Information
                  </h4>

                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Make</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData?.make || ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Model</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData?.model || ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Color</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData?.color || ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>License Plate</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData?.license_plate || ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>VIN</label>
                        <input
                          type="text"
                          className="form-control"
                          value={formData?.vin || ''}
                          readOnly
                          style={{ backgroundColor: '#f5f5f5' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Approval Information */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  padding: '20px', 
                  marginBottom: '30px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}>
                  <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                    Authorization Information
                  </h4>

                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData?.approval_name || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData?.approval_email || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Address</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData?.approval_address || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData?.approval_phone || ''}
                      readOnly
                      style={{ backgroundColor: '#f5f5f5' }}
                    />
                  </div>
                </div>

                <div style={{ 
                  backgroundColor: '#fff3cd', 
                  padding: '20px', 
                  marginBottom: '30px',
                  borderRadius: '4px',
                  border: '1px solid #ffc107'
                }}>
                  <div className="checkbox">
                    <label style={{ fontSize: '14px', lineHeight: '1.6', color: '#000' }}>
                      <input
                        type="checkbox"
                        checked={certificationChecked}
                        onChange={(e) => setCertificationChecked(e.target.checked)}
                        style={{ marginRight: '10px' }}
                      />
                      <strong>I certify that I meet the definition of a property owner, for purposes of authorization*.</strong>
                    </label>
                  </div>
                  <p style={{ fontSize: '12px', marginTop: '15px', marginBottom: '0', lineHeight: '1.6', color: '#000' }}>
                    *Pursuant to PUC Rule 6501(u), "property owner" means (I) the owner or lessee of the private property or public property; (II) a person who has been
                    authorized to act as an authorized agent for the property owner or lessee of the private property or public property; or (III) a federal, state, county,
                    municipal, or other government entity that is the owner or lessee of the private property or public property, or such entity's employees responsible
                    for such property. In addition, a person, pursuant to §§ 40-10.1-805(1)(a)(II)(B) and (C), C.R.S., (e.g., an HOA board member) may be considered a
                    property owner, for purposes of authorizing a nonconsensual tow. Please note that state law requires a vehicle immobilization on residential private
                    property to be authorized by the owner or lessee of the private property, their direct employees, or direct employees of a retained property
                    management company.
                  </p>
                </div>

                {/* Signature Section */}
                <div style={{ 
                  backgroundColor: '#fff', 
                  padding: '20px', 
                  marginBottom: '30px',
                  borderRadius: '4px',
                  border: '1px solid #ddd'
                }}>
                  <h4 style={{ marginTop: '0', marginBottom: '20px', color: '#000' }}>
                    Signature
                  </h4>

                  <div className="form-group">
                    <label>Signature Type</label>
                    <div>
                      <label className="radio-inline">
                        <input
                          type="radio"
                          value="draw"
                          checked={signatureType === 'draw'}
                          onChange={(e) => setSignatureType(e.target.value)}
                        />
                        Draw Signature
                      </label>
                      <label className="radio-inline" style={{ marginLeft: '20px' }}>
                        <input
                          type="radio"
                          value="text"
                          checked={signatureType === 'text'}
                          onChange={(e) => setSignatureType(e.target.value)}
                        />
                        Type Signature
                      </label>
                    </div>
                  </div>

                  {signatureType === 'draw' ? (
                    <div className="form-group">
                      <label>Draw Your Signature Below</label>
                      <SignatureCanvas
                        signatureData={signatureImage}
                        onSignatureChange={setSignatureImage}
                      />
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>Type Your Full Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={signatureText}
                        onChange={(e) => setSignatureText(e.target.value)}
                        onBlur={handleSignatureComplete}
                        placeholder="Type your full name"
                        style={{ fontFamily: '"Brush Script MT", cursive', fontSize: '24px' }}
                      />
                    </div>
                  )}
                </div>

                {/* Submit Button */}
                <div style={{ textAlign: 'center', marginTop: '30px' }}>
                  <button
                    type="submit"
                    className="btn yellow_btn"
                    style={{ fontSize: '18px', padding: '12px 40px' }}
                    disabled={loading}
                  >
                    {loading ? 'Submitting...' : 'Submit Authorization'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BootAuthorizationForm;
